import { FilterPills } from "components/FilterPills";
import { ILabeledField, ISearchFilterOperationArtifact } from "operations/search-filter-operation";
import { IArtifactComponentProps } from "types/services";
import { fromToolInputToUIFilters, resolveSchoolDistrictLabel } from "components/Filters/Filters.def";
import { useEffect, useMemo, useState } from "react";
import { isProduction } from "scripts/application/setting";
import { captureException } from "scripts/logging/error-report";

export type ISearchListingsFiltersParams = {
    search_id: string;
    //search_name: N;
}

export type ISearchListingsFiltersProps = IArtifactComponentProps<
    ISearchListingsFiltersParams,
    ISearchFilterOperationArtifact<"search_listings">
>;

SearchListingsFilters.artifactKey = "search_filter_operation";

// A district resolved from its ID, tagged with the ID it was resolved FROM so a
// stale name can never be painted onto a different artifact's district.
interface IResolvedDistrict {
    key: string;
    field: ILabeledField<string>;
}

// Names already resolved in this page session, keyed by district ID. Component
// state alone is not enough: it dies with the component, so every remount
// restarted at `label: null` and the chip flipped "27" -> "Katy ISD" again (the
// lookup itself is cached, so this was a visible flicker rather than a refetch).
// Reading it during render lets a remount paint the resolved name immediately.
const resolvedDistrictLabels = new Map<string, ILabeledField<string>>();

// Bounded so a long-lived SPA session cannot accumulate entries without limit.
// No TTL, unlike the request cache behind `resolveSchoolDistrictLabel`
// (services/index.ts): a district's NAME does not change, and an expiry would
// put the chip back to painting the bare ID on the next remount — exactly the
// flicker this cache exists to prevent.
const MAX_RESOLVED_DISTRICTS = 50;

/** Canonical cache key — the same trim `resolveSchoolDistrictLabel` applies
 *  before its lookup, so `" 27 "` and `"27"` share one entry. */
const districtCacheKey = (value: string | number) => String(value).trim();

function rememberDistrictLabel(key: string, field: ILabeledField<string>): void {
    // A Map iterates in insertion order, so the first key is the oldest.
    if (!resolvedDistrictLabels.has(key) && resolvedDistrictLabels.size >= MAX_RESOLVED_DISTRICTS) {
        const oldest = resolvedDistrictLabels.keys().next().value;
        if (oldest !== undefined) { resolvedDistrictLabels.delete(oldest); }
    }
    resolvedDistrictLabels.set(key, field);
}

export function SearchListingsFilters(props: ISearchListingsFiltersProps): JSX.Element {
    const artifact = loadArtifact(props);
    const toolInput = artifact?.filters;
    const baseFilters = useMemo(() => toolInput ? fromToolInputToUIFilters(toolInput) : undefined, [toolInput]);
    const [resolvedDistrict, setResolvedDistrict] = useState<IResolvedDistrict | null>(null);

    // The school district usually arrives as a bare ID (HAR.com's search URL spells
    // its district param `school_district` and puts an ID in it). The server resolves
    // the name in `_resolve_school_district`, but only the server's copy of the
    // metadata carries it: the optimistic artifact this component renders first is
    // built client-side with `label: null`, so the pill would read "27" instead of
    // "Katy ISD" for as long as the search runs. Resolve it here too — the lookup is
    // cached and de-duplicated, and leaves the field alone when there is nothing to
    // resolve (already labeled, a real name, or a failed lookup).
    useEffect(() => {
        const district = baseFilters?.school_district;
        // `label != null`, not truthiness: `""` is the server's "asked HAR, no such
        // district", so re-asking only 404s again (and misses aren't cached). A server
        // lookup that never got an answer leaves `label: null`, which we do retry.
        if (!district?.value || district.label != null) { return; }

        let cancelled = false;
        const key = districtCacheKey(district.value);
        // Already known from an earlier mount — the render below picked it up
        // straight out of the cache, so there is nothing to wait for.
        if (resolvedDistrictLabels.has(key)) { return; }

        resolveSchoolDistrictLabel(district)
            .then((field) => {
                if (!field?.label) { return; }
                rememberDistrictLabel(key, field);
                if (cancelled) { return; }
                setResolvedDistrict({ key, field });
            })
            .catch(captureException);

        return () => { cancelled = true; };
    }, [baseFilters]);

    const filters = useMemo(() => {
        const district = baseFilters?.school_district;
        if (!baseFilters || !district?.value || district.label != null) { return baseFilters; }

        const key = districtCacheKey(district.value);
        // Only adopt a resolution of the district we are actually showing: state
        // first (this mount resolved it), then the cross-mount cache.
        const field = resolvedDistrict?.key === key
            ? resolvedDistrict.field
            : resolvedDistrictLabels.get(key);
        if (!field) { return baseFilters; }

        return { ...baseFilters, school_district: field };
    }, [baseFilters, resolvedDistrict]);

    if (!artifact) {
        console.error(
            "No artifact found for 'SearchListingsFilters' with the given search_id:",
            props.search_id
        );

        return <></>;
    }

    if (!filters) {
        console.error('No filters found in artifact for SearchListingsFilters with the given search_id:', props.search_id, props);
        return <></>;
    }

    // const [filters, setFilters] = useState({} as IFilters);

    // useEffect(() => {
    //     let isCurrent = true;

    //     async function fetchFilters() {
    //         const newFilters = await transformApiFilters(input);
    //         if (isCurrent) { setFilters(newFilters); }
    //     }

    //     fetchFilters();
    //     return () => { isCurrent = false; };
    // }, []);

    // const messagePrefix = getMessagePrefix(search_name, type);
    // const Pills = getPills(search_name, filters);
    return <FilterPills filters={filters} />;
}

SearchListingsFilters.asText = function (props: ISearchListingsFiltersProps): string {

    // artifact_text not implemented for this component, so we will generate a text representation of the filters instead
    const artifact = loadArtifact(props);
    if (!artifact) {
        return isProduction ? "" : "SearchListingsOutput asText Not Implemented!";
    }

    if (artifact.artifact_text) {
        return artifact.artifact_text;
    }

    // Tool-input values are nested (ILabeledField { value, label }, { min, max }
    // ranges, arrays), so template-interpolating them directly yields
    // "[object Object]". Flatten each to a readable string instead.
    const format = (value: unknown): string => {
        if (value == null) return "";
        if (Array.isArray(value)) return value.map(format).filter(Boolean).join("/");
        if (typeof value === "object") {
            const obj = value as Record<string, unknown>;
            // ILabeledField prefers its label; otherwise join a plain record's values.
            if ("label" in obj || "value" in obj) return String(obj.label ?? obj.value ?? "");
            return Object.values(obj).map(format).filter(Boolean).join("-");
        }
        return String(value);
    };

    // `filters_encoding` is provenance for the decoder (see isLegacyEncodedArtifact),
    // not a filter the user chose, so it must not appear in this listing.
    const filtersContent = Object.entries(artifact.filters)
        .filter(([key]) => key !== "filters_encoding")
        .map(([key, value]) => [key, format(value)] as const)
        .filter(([, value]) => value !== "")
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");

    return `${filtersContent}`;
}

function loadArtifact(props: ISearchListingsFiltersProps): ISearchFilterOperationArtifact<"search_listings"> | undefined {
    const { search_id, artifacts } = props;
    if (!Array.isArray(artifacts)) return undefined;
    const artifact = search_id ? artifacts.find((x) => x.search_id === search_id) : undefined;
    return artifact;
}

// function getMessagePrefix(searchName: string, type: eQueryType) {
//     const isSearch = type === eQueryType.Search;
//     switch (searchName) {
//         case "search_listings": return isSearch ? 'Show me properties based on the following filters' : 'Filter Property By';
//         default:
//             console.error("SearchFilters: search_name not recognized:", searchName);
//             return isProduction ? <></> : <>SearchFilters: {searchName} not recognized</>;
//     }
// }

// function getPills(searchName: string, filters: any): JSX.Element {
//     switch (searchName) {
//         case "search_listings": return <FilterPills filters={filters} />;
//         default: return <></>;
//     }
// }